import { DIRECTIONS, REVERSE_DIRECTION_MAPPING } from "../globals";
import { NavDirection, NavigationData, SceneComponent } from "./types";
import { selectId, selectInitialFocus, selectNavigation } from "./utils";

type NavGraphEdge = {
	readonly from: string;
	readonly to: string;
	readonly direction: NavDirection;
};

/**
 * Creates a single directed navigation graph edge.
 *
 * @pure
 * @param from - Starting node ID
 * @param to - Destination node ID
 * @param direction - Direction of navigation from `from` to `to`
 * @returns A directed edge object
 */
export const navGraphEdge = (from: string, to: string, direction: NavDirection): NavGraphEdge => ({
	from,
	to,
	direction,
});

/**
 * @pure
 * Creates two reciprocal edges between two navigation nodes, one
 * in the given direction and one in the opposite direction.
 *
 * @param nodeA - First node ID
 * @param nodeB - Second node ID
 * @param direction - Direction from `nodeA` to `nodeB`
 * @returns An array containing both directed edges
 */
export const navGraphBidirectionalEdge = (
	nodeA: string,
	nodeB: string,
	direction: NavDirection,
): NavGraphEdge[] => {
	const edges: NavGraphEdge[] = [];
	const oppositeDirection = REVERSE_DIRECTION_MAPPING[direction];

	edges.push(navGraphEdge(nodeA, nodeB, direction));
	edges.push(navGraphEdge(nodeB, nodeA, oppositeDirection));

	return edges;
};

/**
 * @pure
 * Creates bidirectional edges between consecutive navigation nodes.
 * For each adjacent pair, adds one edge in the given direction and
 * one in the opposite direction.
 *
 * @param nodes - Ordered list of navigation nodes.
 * @param direction - Direction to apply from each node to the next.
 * @returns Array of edges representing the bidirectional links.
 */
export const navGraphChain = (nodes: string[], direction: NavDirection): NavGraphEdge[] => {
	const edges: NavGraphEdge[] = [];

	for (let i = 0; i < nodes.length - 1; i++) {
		edges.push(...navGraphBidirectionalEdge(nodes[i], nodes[i + 1], direction));
	}

	return edges;
};

/**
 * Updates scene components with navigation links based on the given edges.
 *
 * @pure
 * @param components - The scene components to update.
 * @param navEdges - Directed edges describing new navigation relationships.
 * @returns A new array of components with updated navigation connections.
 */
export const navGraphUpdateEdges = (
	components: SceneComponent[],
	navEdges: NavGraphEdge[],
): SceneComponent[] => {
	const connections = new Map<string, NavigationData>(
		components.map((c) => [selectId(c), { ...c.navigation }]), // copy on write
	);

	navEdges.forEach((edge) => {
		const fromNav = connections.get(edge.from);
		if (fromNav) {
			fromNav[edge.direction] = edge.to;
		}
	});

	return components.map((c) => {
		const id = selectId(c);
		return {
			...c,
			navigation: {
				// TODO: I can't quite figure out how to get this type correct it was constructed from the inital nav values so SHOULD have the data
				...connections.get(id)!,
			},
		};
	});
};

/**
 * Removes specified nodes from the navigation graph and reconnects any affected neighbors.
 *
 * Any broken navigation links caused by removed nodes are updated so that neighboring nodes
 * point to each other if applicable. The function does not mutate the original components.
 *
 * @param components - The scene components representing the navigation graph.
 * @param toRemove - Nodes to remove from the graph.
 * @returns A new array of components with the specified nodes removed and navigation updated.
 */
export const navGraphPruneNodes = (
	components: SceneComponent[],
	toRemove: SceneComponent[],
): SceneComponent[] => {
	const graph: Map<string, SceneComponent> = components.reduce((map, data) => {
		return map.set(selectId(data), { ...data });
	}, new Map());

	for (const data of toRemove) {
		const nodeId = selectId(data);
		const node = graph.get(nodeId);

		if (!node) {
			console.error("node does not exist in graph");
			continue;
		}

		if (selectInitialFocus(node)) {
			console.warn(`removing node with initial focus ${data} can cause issues`);
		}

		const navData = selectNavigation(node);

		// Update connections for each neighbor
		for (const direction of DIRECTIONS) {
			const neighborId = navData[direction];
			if (!neighborId) {
				continue;
			}

			const neighbor = graph.get(neighborId);
			if (!neighbor) {
				continue;
			}

			const neighborNav = selectNavigation(neighbor);
			const reverseDirection = REVERSE_DIRECTION_MAPPING[direction];

			// Connect neighbor to the node that was on the opposite side of the removed node
			neighborNav[reverseDirection] = navData[reverseDirection];

			graph.set(neighborId, { ...neighbor, navigation: { ...neighborNav } });
		}

		graph.delete(nodeId);
	}

	return Array.from(graph.values());
};

/**
 * Updates the initial focus of a navigation graph.
 *
 * Marks the component with the given ID as having initial focus, and
 * clears it for all other components. Does not mutate the input array.
 *
 * @note If multiple components share the same ID, all of them will be
 *       marked as focused, which may cause unexpected behavior.
 *
 * @param focusedId - The ID of the component to receive initial focus.
 * @param components - The scene components to update.
 * @returns A new array of components with updated initial focus flags.
 */
export const navGraphUpdateInitialFocus = (focusedId: string, components: SceneComponent[]) => {
	// TODO: find a more elegant way to resolve this issue
	const count = components.filter((c) => selectId(c) === focusedId).length;
	if (count > 1) {
		console.warn(
			`Multiple components share the same ID (${focusedId}). ` +
				`All of them will be marked as initially focused.`,
		);
	}
	return components.map((c) => ({
		...c,
		navigation: {
			...c.navigation,
			initialFocus: selectId(c) === focusedId,
		},
	}));
};
